<script>
import { mapGetters } from 'vuex';
import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { usePhoneNumberMask } from 'dashboard/composables/usePhoneNumberMask';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import AvatarPreview from 'next/avatar/AvatarPreview.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WacallsCallButton from 'dashboard/components-next/Conversation/WacallsCallButton.vue';
import FlexcallsCallButton from 'dashboard/components-next/Conversation/FlexcallsCallButton.vue';
import VideoMeetingButton from 'dashboard/components-next/Conversation/VideoMeetingButton.vue';
import WhatsappCallButton from 'dashboard/components-next/Conversation/WhatsappCallButton.vue';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import {
  formatCapitalSocial,
  companyTipo,
} from 'dashboard/helper/companyHelper';
import { CONTACT_PERMISSIONS } from 'dashboard/constants/permissions';
import ContactAPI from 'dashboard/api/contacts';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    AvatarPreview,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    WacallsCallButton,
    FlexcallsCallButton,
    VideoMeetingButton,
    WhatsappCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['panelClose'],
  setup(props) {
    const { isAdmin } = useAdmin();
    const phoneRef = computed(() => props.contact.phone_number);
    const { maskedPhoneNumber, canViewPhoneNumber } =
      usePhoneNumberMask(phoneRef);
    return {
      isAdmin,
      maskedPhoneNumber,
      canViewPhoneNumber,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showPhotoPreview: false,
      isResettingAiMemory: false,
      showDeleteModal: false,
      documentInput: '',
      isLookingUp: false,
      isScoringLead: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      accountId: 'getCurrentAccountId',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    // CNPJ que já temos do contato (ex.: decisor da prospecção guarda o CNPJ da
    // empresa em custom_attributes) — usado pra pré-preencher o lookup.
    knownCnpj() {
      const cnpj =
        this.contact?.custom_attributes?.cnpj || this.additionalAttributes.cnpj;
      return cnpj ? String(cnpj).replace(/\D/g, '') : '';
    },
    // Saúde/engajamento do número (atualiza com envios: lido +5, falhou -10).
    // Só aparece quando há dados (status != unknown).
    qualityBadge() {
      const status = this.contact.quality_status;
      if (!status || status === 'unknown') return null;
      const map = {
        excellent: 'bg-n-teal-3 text-n-teal-11',
        good: 'bg-n-teal-3 text-n-teal-11',
        average: 'bg-n-amber-3 text-n-amber-11',
        poor: 'bg-n-amber-3 text-n-amber-11',
        critical: 'bg-n-ruby-3 text-n-ruby-11',
      };
      return {
        label: this.$t(`CONTACT_PANEL.QUALITY.${status.toUpperCase()}`),
        score: this.contact.quality_score,
        klass: map[status] || 'bg-n-slate-3 text-n-slate-11',
      };
    },
    // Score de intenção de compra do lead (IA) — persistido em
    // custom_attributes por Agno::LeadScoringService.
    intentBadge() {
      const attrs = this.contact?.custom_attributes || {};
      const raw = attrs.lia_intent_score;
      if (raw === null || raw === undefined || raw === '') return null;
      const score = parseInt(raw, 10);
      if (Number.isNaN(score)) return null;
      const label = attrs.lia_intent_label;
      const map = {
        hot: 'bg-n-ruby-3 text-n-ruby-11',
        warm: 'bg-n-amber-3 text-n-amber-11',
        cold: 'bg-n-slate-3 text-n-slate-11',
      };
      return {
        score,
        reasoning: attrs.lia_intent_reasoning || '',
        klass: map[label] || 'bg-n-slate-3 text-n-slate-11',
      };
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
    currentChat() {
      return this.$store.getters.getSelectedChat;
    },
    currentUser() {
      return this.$store.getters.getCurrentUser;
    },
    canEditContact() {
      const perms = getUserPermissions(this.currentUser, this.accountId);
      if (perms.includes('view_contact_phone_number')) {
        return false;
      }
      if (this.isAdmin) {
        return true;
      }
      return perms.includes(CONTACT_PERMISSIONS);
    },
    assignableAgents() {
      const inboxId = this.currentChat?.inbox_id;
      if (!inboxId) {
        return [];
      }
      return this.$store.getters['inboxAssignableAgents/getAssignableAgents'](
        inboxId
      );
    },
    isAgentForInbox() {
      const userId = this.currentUser?.id;
      return this.assignableAgents.some(agent => agent.id === userId);
    },
    companyAge() {
      const date =
        this.contact.company?.additional_attributes?.data_inicio_atividade;
      if (!date) return null;
      const start = new Date(date);
      if (Number.isNaN(start.getTime())) return null;
      const now = new Date();
      let years = now.getFullYear() - start.getFullYear();
      let months = now.getMonth() - start.getMonth();
      if (months < 0) {
        years -= 1;
        months += 12;
      }
      if (years > 0)
        return `${years} ano${years > 1 ? 's' : ''}${months > 0 ? ` e ${months} ${months > 1 ? 'meses' : 'mês'}` : ''}`;
      return months > 0
        ? `${months} ${months > 1 ? 'meses' : 'mês'}`
        : 'Menos de 1 mês';
    },
    companyCapitalSocial() {
      return formatCapitalSocial(
        this.contact.company?.additional_attributes?.capital_social
      );
    },
    companyNaturezaJuridica() {
      return this.contact.company?.additional_attributes?.natureza_juridica;
    },
    companyTipoLabel() {
      return companyTipo(this.contact.company?.additional_attributes);
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        // Pré-preenche o campo de documento com o CNPJ que já temos (ex.: decisor
        // vindo da prospecção guarda o CNPJ da empresa em custom_attributes) —
        // assim é só clicar pra puxar todos os dados, sem digitar.
        this.documentInput =
          !this.contact?.cpf && !this.contact?.cnpj ? this.knownCnpj : '';

        // BUGFIX: Validate contact ID before fetching to prevent 404 errors
        if (id && id !== 'undefined' && !Number.isNaN(Number(id))) {
          this.$store
            .dispatch('contacts/fetchContactableInbox', id)
            .catch(error => {
              // Silently handle 404 errors for deleted contacts
              if (error?.response?.status === 404) {
                // eslint-disable-next-line no-console
                console.warn(`[ContactInfo] Contact ${id} not found (deleted)`);
              } else {
                // eslint-disable-next-line no-console
                console.error(
                  '[ContactInfo] Error fetching contactable inbox:',
                  error
                );
              }
            });
        }
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    async lookupDocument() {
      const raw = this.documentInput.replace(/\D/g, '');
      if (!raw) return;

      const contactId = this.contact.id;
      if (!contactId) {
        useAlert(this.$t('CONTACT_PANEL.LOOKUP_ERROR'));
        return;
      }

      if (raw.length === 11) {
        // CPF — just save it to the contact directly
        try {
          await this.$store.dispatch('contacts/update', {
            id: contactId,
            cpf: this.documentInput.trim(),
          });
          useAlert(this.$t('CONTACT_PANEL.LOOKUP_SUCCESS'));
          this.documentInput = '';
        } catch {
          useAlert(this.$t('CONTACT_PANEL.LOOKUP_ERROR'));
        }
        return;
      }

      if (raw.length !== 14) {
        useAlert(this.$t('CONTACT_PANEL.LOOKUP_ERROR'));
        return;
      }

      // CNPJ — lookup via API and enrich contact
      this.isLookingUp = true;
      try {
        await ContactAPI.cnpjLookup(this.documentInput.trim(), contactId);
        // Refresh contact in store after enrichment
        await this.$store.dispatch('contacts/show', { id: contactId });
        useAlert(this.$t('CONTACT_PANEL.LOOKUP_SUCCESS'));
        this.documentInput = '';
      } catch (error) {
        const status = error?.response?.status;
        if (status === 404) {
          useAlert(this.$t('CONTACT_PANEL.LOOKUP_NOT_FOUND'));
        } else {
          useAlert(this.$t('CONTACT_PANEL.LOOKUP_ERROR'));
        }
      } finally {
        this.isLookingUp = false;
      }
    },
    // Reavalia o score de intenção do lead (IA) na conversa atual e atualiza
    // o contato na store.
    async reevaluateLead() {
      const conversationId = this.currentChat?.id;
      if (!conversationId || this.isScoringLead) return;
      this.isScoringLead = true;
      try {
        await ConversationAPI.scoreLead({ conversationId });
        await this.$store.dispatch('contacts/show', { id: this.contact.id });
        useAlert(this.$t('CONTACT_PANEL.INTENT_SCORE.REEVALUATED'));
      } catch {
        useAlert(this.$t('CONTACT_PANEL.INTENT_SCORE.ERROR'));
      } finally {
        this.isScoringLead = false;
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
    async resetAiMemory() {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('CONTACT_PANEL.RESET_AI_MEMORY_CONFIRM'))) {
        return;
      }
      this.isResettingAiMemory = true;
      try {
        await ContactAPI.resetAiMemory(this.contact.id);
        useAlert(this.$t('CONTACT_PANEL.RESET_AI_MEMORY_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CONTACT_PANEL.RESET_AI_MEMORY_ERROR'));
      } finally {
        this.isResettingAiMemory = false;
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right">
      <div class="flex flex-row justify-between">
        <button
          v-if="showAvatar && contact.thumbnail"
          type="button"
          class="p-0 border-0 rounded-full cursor-zoom-in bg-transparent hover:opacity-90"
          :aria-label="$t('CONTACT_PANEL.VIEW_PHOTO')"
          @click="showPhotoPreview = true"
        >
          <Avatar
            :src="contact.thumbnail"
            :name="contact.name"
            :status="contact.availability_status"
            :size="48"
            hide-offline-status
            rounded-full
          />
        </button>
        <Avatar
          v-else-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="48"
          hide-offline-status
          rounded-full
        />
      </div>
      <AvatarPreview
        v-if="showPhotoPreview"
        :src="contact.avatar_full_url || contact.thumbnail"
        :name="contact.name"
        @close="showPhotoPreview = false"
      />

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info text-sm text-n-slate-10"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link text-sm text-n-slate-10" />
            </a>
          </div>
        </div>

        <span
          v-if="qualityBadge"
          v-tooltip.right="$t('CONTACT_PANEL.QUALITY.TOOLTIP')"
          class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium"
          :class="qualityBadge.klass"
        >
          <span class="i-lucide-activity text-xs" />
          {{ qualityBadge.label }} · {{ qualityBadge.score }}
        </span>

        <div class="flex items-center gap-1">
          <span
            v-if="intentBadge"
            v-tooltip.right="
              intentBadge.reasoning || $t('CONTACT_PANEL.INTENT_SCORE.TOOLTIP')
            "
            class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium"
            :class="intentBadge.klass"
          >
            <span class="i-lucide-flame text-xs" />
            {{ $t('CONTACT_PANEL.INTENT_SCORE.LABEL') }} ·
            {{ intentBadge.score }}
          </span>
          <NextButton
            v-if="currentChat && currentChat.id"
            xs
            faded
            slate
            :is-loading="isScoringLead"
            icon="i-lucide-sparkles"
            :label="$t('CONTACT_PANEL.INTENT_SCORE.REEVALUATE')"
            @click="reevaluateLead"
          />
        </div>
        <p
          v-if="intentBadge && intentBadge.reasoning"
          class="text-xs text-n-slate-11 break-words mb-0.5"
        >
          {{ intentBadge.reasoning }}
        </p>

        <p v-if="additionalAttributes.description" class="break-words mb-0.5">
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :href="
              canViewPhoneNumber && contact.phone_number
                ? `tel:${contact.phone_number}`
                : ''
            "
            :value="maskedPhoneNumber"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.identifier && canViewPhoneNumber"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
            show-copy
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <ContactInfoRow
            v-if="contact.cpf"
            :value="contact.cpf"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.CPF')"
            show-copy
          />
          <ContactInfoRow
            v-if="contact.cnpj"
            :value="contact.cnpj"
            icon="contact-identify"
            emoji="🏛️"
            :title="$t('CONTACT_PANEL.CNPJ')"
            show-copy
          />
          <ContactInfoRow
            v-if="companyAge"
            :value="companyAge"
            icon="calendar"
            emoji="📅"
            :title="$t('CONTACT_PANEL.COMPANY_AGE')"
          />
          <ContactInfoRow
            v-if="companyCapitalSocial"
            :value="companyCapitalSocial"
            icon="contact-identify"
            emoji="💰"
            :title="$t('CONTACT_PANEL.CAPITAL_SOCIAL')"
          />
          <ContactInfoRow
            v-if="companyTipoLabel"
            :value="companyTipoLabel"
            icon="building-bank"
            emoji="🏷️"
            :title="$t('CONTACT_PANEL.TIPO_EMPRESA')"
          />
          <ContactInfoRow
            v-if="companyNaturezaJuridica"
            :value="companyNaturezaJuridica"
            icon="building-bank"
            emoji="⚖️"
            :title="$t('CONTACT_PANEL.NATUREZA_JURIDICA')"
          />
          <div
            v-if="canEditContact && !contact.cpf && !contact.cnpj"
            class="flex items-center gap-1 mt-1"
          >
            <input
              v-model="documentInput"
              type="text"
              class="reset-base w-36 h-5 px-1 text-sm bg-transparent border-0 border-b border-n-weak rounded-none text-n-slate-11 placeholder:text-n-slate-10 focus:border-n-brand focus:outline-none"
              :placeholder="$t('CONTACT_PANEL.DOCUMENT_PLACEHOLDER')"
              :disabled="isLookingUp"
              @keydown.enter="lookupDocument"
            />
            <NextButton
              v-if="documentInput.trim()"
              ghost
              xs
              slate
              :icon="isLookingUp ? 'i-lucide-loader-2' : 'i-lucide-search'"
              :disabled="isLookingUp"
              @click="lookupDocument"
            />
          </div>
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center w-full mt-0.5 gap-2">
        <ComposeConversation
          :contact-id="String(contact.id)"
          is-modal
          @close="closeComposeConversationModal"
        >
          <template #trigger="{ toggle }">
            <NextButton
              v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
              icon="i-ph-chat-circle-dots"
              slate
              faded
              sm
              @click="openComposeConversationModal(toggle)"
            />
          </template>
        </ComposeConversation>
        <WhatsappCallButton :conversation="currentChat" />
        <WacallsCallButton :conversation="currentChat" />
        <FlexcallsCallButton :conversation="currentChat" />
        <VideoMeetingButton :conversation="currentChat" />
        <NextButton
          v-if="canEditContact"
          v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
          icon="i-ph-pencil-simple"
          slate
          faded
          sm
          @click="toggleEditModal"
        />
        <NextButton
          v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
          icon="i-ph-arrows-merge"
          slate
          faded
          sm
          :disabled="uiFlags.isMerging"
          @click="openMergeModal"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('CONTACT_PANEL.RESET_AI_MEMORY')"
          icon="i-ph-brain"
          slate
          faded
          sm
          :disabled="isResettingAiMemory"
          @click="resetAiMemory"
        />
        <NextButton
          v-if="isAdmin"
          v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
          icon="i-ph-trash"
          slate
          faded
          sm
          ruby
          :disabled="uiFlags.isDeleting"
          @click="toggleDeleteModal"
        />
      </div>
      <EditContact
        v-if="showEditModal"
        :show="showEditModal"
        :contact="contact"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>
